import React, { useState } from 'react';

import { useForm, zodResolver } from '@mantine/form';
import { t } from 'i18next';
import * as z from 'zod';
import { observer } from 'mobx-react';
import { AppMedError } from '../../../global/error/AppMedError.ts';

import { useNavigate } from 'react-router-dom';
import { Button, Center, Flex, InputWrapper, Notification, Paper, Stack } from '@mantine/core';
import { useSessionProvider } from '../../../global/provider/sessionContext.tsx';
import { usePutTask } from '../../../global/entities/Tasks/mutations/usePutTaskMutations.tsx';
import { useGetTaskSummaries } from '../../../global/entities/Tasks/queries/useGetTaskSummaries.tsx';
import { DateTimePicker } from '@mantine/dates';
import { useParams } from 'react-router';
import { IconCalendar } from '@tabler/icons-react';

const TdmForm: React.FC = observer(() => {
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const navigate = useNavigate();
    const { currentUser } = useSessionProvider();
    const { refetch: refetchTaskSummaries } = useGetTaskSummaries();
    const { id } = useParams<{ id: string }>();

    const putTask = usePutTask();

    const schema = z.object({
        completionDate: z.date(),
    });

    const form = useForm({
        initialValues: {
            completionDate: new Date(),
        },
        validate: zodResolver(schema),
    });

    const handleCancel = () => {
        form.reset();
        navigate('/patientDashboard/mainPatient');
    };

    const handleSubmit = async (formValues: { completionDate: Date }) => {
        setError(null);
        setIsSubmitting(true);

        if (!currentUser?.patientID || !id) 
            return;

        try {
            await putTask.mutateAsync({
                taskId: id,
                variables: { completionDate: formValues.completionDate },
            });

            console.log(t('submissionSuccess'));
        } catch (e) {
            if (e instanceof AppMedError) 
                setError(e.message);
             else 
                setError('An unexpected error occurred.');
            
        } finally {
            setIsSubmitting(false);
            refetchTaskSummaries();
            handleCancel();
        }
    };

    return (
        <Center w={'100%'}>
            <Paper mt={'lg'} miw={400}>
                <form
                    onSubmit={form.onSubmit((values) => {
                        handleSubmit(values);
                    })}
                >
                    <Stack gap={'md'}>
                        <InputWrapper
                            styles={{ label: { fontSize: 16, fontWeight: 600 } }}
                            label={t('tdmForm.completionDateTitle')}
                            withAsterisk
                        >
                            <DateTimePicker
                                maw={250}
                                leftSection={<IconCalendar size={20} />}
                                valueFormat={'YYYY-MM-DD HH:mm'}
                                placeholder={t('tdmForm.completionDatePlaceholder')}
                                styles={{
                                    input: {
                                        textBoxTrim: 'trim-end',
                                    },
                                }}
                                {...form.getInputProps('completionDate')}
                            />
                        </InputWrapper>
                        <Flex justify={'space-evenly'} gap={'md'}>
                            <Button fullWidth variant="default" onClick={handleCancel}>
                                {t('button.cancel')}
                            </Button>
                            <Button fullWidth loading={isSubmitting} type="submit">
                                {t('button.save')}
                            </Button>
                        </Flex>
                    </Stack>
                </form>
                {error && (
                    <Notification color="red" onClose={() => setError(null)}>
                        {error}
                    </Notification>
                )}
            </Paper>
        </Center>
    );
});

export default TdmForm;
